// #397: the read copies both apps keep under `jl.asyncList.<key>` (the
// asyncListCache adapters) are the signed-in account's. On an account change
// they go; the program editor's drafts, under the same prefix but scoped
// `editorDraft.<account>` by the hosts, are work and stay.
const PREFIX = "jl.asyncList.";

// #398: mobile's catalogue copy (apps/mobile/src/state/exercisesCache.ts), a
// key from before the prefix: kept where it is (moving it wants a migration)
// and named here, so it is gated and dropped like the rest.
export const CATALOG_COPY_PREFIX = "jackedlogs.exercisesCache.";

export const isAccountReadCopyKey = (key: string): boolean =>
  (key.startsWith(PREFIX) && !key.startsWith(`${PREFIX}editorDraft.`)) ||
  key.startsWith(CATALOG_COPY_PREFIX);

// The account the copies belong to, as a number: each app's dropAccountCaches
// moves it. A read started under one generation writes no copy once it has
// moved (a sign-out, a sign-in): the account it read for is gone, and its
// answer landing late would put that account's rows back on the device.
let generation = 0;

export const accountGeneration = (): number => generation;

/** Called first by dropAccountCaches, before the copies are deleted. */
export const nextAccountGeneration = (): void => {
  generation += 1;
};

/** The account a read starts under, taken when the read starts. `current()`
 *  is false once dropAccountCaches has moved the generation: the account it
 *  read for is gone, so the answer writes no copy. Closes over the number so
 *  no caller holds (or forgets to recheck) a bare generation. */
export type AccountScope = { current(): boolean };

export const accountScope = (): AccountScope => {
  const taken = generation;
  return { current: () => taken === generation };
};

// lsCache's own prefix (apps/web-rnw/src/lsCache.ts): everything under it is a
// copy of the signed-in account's rows, never work.
export const LS_COPY_PREFIX = "jl.lsCache.";

/** #398: the write side every account copy shares (ListCache,
 *  ResourceCache, ProgramsCache, CatalogSnapshotStore, lsCache). `scope` is
 *  the one the writer took with `accountScope()` when the read (or the
 *  computation) its value comes from started: never optional, so a late
 *  answer for an account that has left cannot reach the disk unchecked.
 *  Resolves false when the scope refused the copy (the account has changed),
 *  so a writer keeps its memory in step with the disk; a storage failure is
 *  best-effort as before and still resolves true. */
export type CopyWrite<T> = (value: T, scope: AccountScope) => Promise<boolean>;

export type CopyStore<T> = {
  read: () => Promise<T | null>;
  write: CopyWrite<T>;
};

/** #398: the account gate of one disk store, for the adapters (both
 *  asyncListCache, its `.web` twin, lsCache, mobile's catalogue) so no
 *  caller checks by hand. True when the write or clear may go: the caller's
 *  scope is still current, or the key is not an account copy (the editor
 *  drafts are work, never gated). The caller's scope alone decides. */
export const scopeGate = (storageKey: string): ((scope: AccountScope) => boolean) =>
  isAccountReadCopyKey(storageKey) || storageKey.startsWith(LS_COPY_PREFIX)
    ? (scope) => scope.current()
    : () => true;

/** #398: a module-level memo of the signed-in account's data (a window, a
 *  single-flight map, a fallback). The getter returns the memo's object, and
 *  a fresh `init()` once the generation has moved since it was made: what
 *  the last account left in memory is never served to the next one. Take the
 *  object BEFORE an await and write into that: a write landing after the
 *  account changed then fills an orphan, never the new account's memo. */
export const accountMemo = <T extends object>(init: () => T): (() => T) => {
  let scope = accountScope();
  let value = init();
  return () => {
    if (!scope.current()) {
      value = init();
      scope = accountScope();
    }
    return value;
  };
};
